import { usePreferredLocaleContext } from "components/context/user/PreferredLocaleContext"
import React from "react"
import { reportErrorMessage } from "utils/ErrorReporting"

export enum SupportedLocale {
  UnitedStatesEnglish = "en-US",
  UnitedStatesSpanish = "es-US",
  CanadianFrench = "fr-CA"
}

enum SupportedLocaleDisplayName {
  UnitedStatesEnglish = "English (U.S.)",
  UnitedStatesSpanish = "Español (EE. UU.)",
  CanadianFrench = "Français (Canada)"
}
const displayNamesBySupportedLocale = {
  [SupportedLocale.UnitedStatesEnglish]: SupportedLocaleDisplayName.UnitedStatesEnglish,
  [SupportedLocale.UnitedStatesSpanish]: SupportedLocaleDisplayName.UnitedStatesSpanish,
  [SupportedLocale.CanadianFrench]: SupportedLocaleDisplayName.CanadianFrench
} as const

export const defaultLocale = SupportedLocale.UnitedStatesEnglish

export function getSupportedLocaleDisplayName(locale: SupportedLocale): string {
  return displayNamesBySupportedLocale[locale]
}

function resolveLocaleMessageFile(locale: SupportedLocale): `${string}.json` {
  switch (locale) {
    case SupportedLocale.UnitedStatesSpanish:
      return "es-US.json"
    case SupportedLocale.CanadianFrench:
      return "fr-CA.json"
    case SupportedLocale.UnitedStatesEnglish:
    default:
      return "en-US.json"
  }
}

type CompiledMessage = {
  type: number
  value: string
}

type MessageId = string
type CompiledMessages = Record<MessageId, CompiledMessage[]>

function loadLocaleMessages(locale: SupportedLocale): Promise<CompiledMessages> {
  const messageFile = resolveLocaleMessageFile(locale)
  return import(`compiledLang/${messageFile}`)
}

export function useIntlMessages(): { didCompiledMessagesLoad: boolean } {
  const { preferredLocale, setCompiledMessages } = usePreferredLocaleContext()
  const [didCompiledMessagesLoad, setDidCompiledMessagesLoad] = React.useState(false)

  React.useEffect(() => {
    async function getLocaleData(): Promise<void> {
      try {
        const messages = await loadLocaleMessages(preferredLocale)

        setCompiledMessages(messages)
        setDidCompiledMessagesLoad(true)
      } catch (err) {
        reportErrorMessage("Could not load locale messages")
      }
    }
    getLocaleData()
  }, [preferredLocale, setCompiledMessages])

  return { didCompiledMessagesLoad }
}

export function getSupportedLocales(countryCode: string): SupportedLocale[] {
  switch (countryCode) {
    case "US":
      return [SupportedLocale.UnitedStatesEnglish, SupportedLocale.UnitedStatesSpanish, SupportedLocale.CanadianFrench]
    // We will need to remove SupportedLocale.CanadianFrench from US before 1/1/2024 --- https://carrotfertility.atlassian.net/browse/OP-720
    case "CA":
      return [SupportedLocale.UnitedStatesEnglish, SupportedLocale.CanadianFrench]
    default:
      return [SupportedLocale.UnitedStatesEnglish]
  }
}
